import { config } from "../config";
import { TSession } from "../utils/types";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const baseUrl = `${protocol}://${host}/clinics/v1/mgmt-clinic`;

export type TClinicApiKey = {
  id: number;
  clinic_uuid: string;
  name: string;
  prefix: string;
  allowed_scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  is_active: boolean;
};

export type TClinicApiKeyListResponse = {
  results: TClinicApiKey[];
};

export type TCreateClinicApiKeyRequest = {
  name: string;
  allowed_scopes: string[];
  expires_at?: string;
};

export type TCreateClinicApiKeyResponse = TClinicApiKey & {
  key: string;
};

export const listClinicApiKeys = async (
  session: TSession,
): Promise<TClinicApiKey[]> => {
  if (!session.clinicId) {
    throw new Error("Clinic identifier is missing from session");
  }

  const response = await fetchWithAuthRetry(
    `${baseUrl}/clinics/${session.clinicId}/api-keys`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to load API keys (${response.status}): ${errorText || "Unknown error"}`,
    );
  }

  const data: TClinicApiKeyListResponse = await response.json();
  return data.results;
};

export const createClinicApiKey = async (
  session: TSession,
  payload: TCreateClinicApiKeyRequest,
): Promise<TCreateClinicApiKeyResponse> => {
  if (!session.clinicId) {
    throw new Error("Clinic identifier is missing from session");
  }

  const response = await fetchWithAuthRetry(
    `${baseUrl}/clinics/${session.clinicId}/api-keys`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create API key (${response.status}): ${errorText || "Unknown error"}`,
    );
  }

  const data: TCreateClinicApiKeyResponse = await response.json();
  return data;
};

export const deleteClinicApiKey = async (
  session: TSession,
  apiKeyId: number,
): Promise<void> => {
  if (!session.clinicId) {
    throw new Error("Clinic identifier is missing from session");
  }

  const response = await fetchWithAuthRetry(
    `${baseUrl}/clinics/${session.clinicId}/api-keys/${apiKeyId}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to delete API key (${response.status}): ${errorText || "Unknown error"}`,
    );
  }
};
